import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/date";

const fmtTs = (v) => formatDateTime(v, { dateStyle: "medium", timeStyle: "short" });

function Cell({ state, title, testId }) {
  const base = "inline-flex h-6 w-6 items-center justify-center rounded-full";
  let el;
  if (state === "approved")
    el = <span className={`${base} bg-emerald-100 text-emerald-700`}><Check className="h-4 w-4" /></span>;
  else if (state === "overdue")
    el = <span className={`${base} bg-red-100 text-red-700`}><X className="h-4 w-4" /></span>;
  else if (state === "assigned")
    el = <span className={`${base} bg-slate-100 text-xs font-bold text-slate-500`}>A</span>;
  else
    el = <span className="text-slate-300">·</span>;
  return <span data-testid={testId} title={title || undefined} className={title ? "cursor-help" : undefined}>{el}</span>;
}

export default function DocumentGrid() {
  const [forms, setForms] = useState([]);
  const [rows, setRows] = useState([]);
  const [formFilter, setFormFilter] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/document-depot/grid");
      setForms(data.forms || []);
      setRows(data.rows || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visibleForms = useMemo(
    () => (formFilter === "all" ? forms : forms.filter((f) => f.id === formFilter)),
    [forms, formFilter]
  );
  const visibleRows = useMemo(
    () => (overdueOnly ? rows.filter((r) => r.has_overdue) : rows),
    [rows, overdueOnly]
  );

  return (
    <div data-testid="document-grid-page">
      <div className="mb-2">
        <Link to="/coach/document-depot"><Button variant="ghost" size="sm" className="rounded-full text-slate-500" data-testid="grid-back-btn"><ArrowLeft className="mr-2 h-4 w-4" /> Back to Document Depot</Button></Link>
      </div>
      <PageHeader title="Team Members Grid" subtitle="Every member's status for each form at a glance." />

      <Card className="mb-4 flex flex-wrap items-center gap-4 p-4">
        <div className="min-w-[220px]">
          <Label className="text-xs text-slate-500">Form</Label>
          <Select value={formFilter} onValueChange={setFormFilter}>
            <SelectTrigger data-testid="grid-form-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="grid-form-all">All forms</SelectItem>
              {forms.map((f) => <SelectItem key={f.id} value={f.id} data-testid={`grid-form-${f.id}`}>#{f.seq} — {f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pt-4">
          <Switch id="overdue-only" checked={overdueOnly} onCheckedChange={setOverdueOnly} data-testid="grid-overdue-toggle" />
          <Label htmlFor="overdue-only" className="cursor-pointer text-sm text-slate-600">Show overdue only</Label>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 pt-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Cell state="approved" /> Approved</span>
          <span className="inline-flex items-center gap-1"><Cell state="overdue" /> Overdue</span>
          <span className="inline-flex items-center gap-1"><Cell state="assigned" /> Assigned</span>
          <span className="text-slate-400">· hover a cell for submission &amp; approval times</span>
        </div>
      </Card>

      {forms.length === 0 || rows.length === 0 ? (
        <EmptyState title="Nothing to show yet" description="Create forms and add approved members to populate the grid." testId="grid-empty" />
      ) : (
        <Card className="overflow-x-auto border-slate-200 shadow-sm">
          <table className="min-w-full border-separate border-spacing-0" data-testid="grid-table">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-500">Member</th>
                {visibleForms.map((f) => (
                  <th
                    key={f.id}
                    title={f.name}
                    className="whitespace-nowrap bg-slate-50 px-3 py-3 text-center text-xs font-bold text-slate-500"
                    data-testid={`grid-col-${f.id}`}
                  >
                    <span className="cursor-help">#{f.seq}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.member_id} className="border-t border-slate-100" data-testid={`grid-row-${row.member_id}`}>
                  <td className="sticky left-0 z-10 whitespace-nowrap border-t border-slate-100 bg-white px-4 py-2 text-sm font-medium text-slate-800">{row.member_name}</td>
                  {visibleForms.map((f) => {
                    const c = row.cells[f.id];
                    const state = c && typeof c === "object" ? c.state : (c || "none");
                    const parts = [];
                    if (c && typeof c === "object") {
                      if (c.submitted_at) parts.push(`Submitted ${fmtTs(c.submitted_at)}`);
                      if (c.approved_at) parts.push(`Approved ${fmtTs(c.approved_at)}`);
                    }
                    return (
                      <td key={f.id} className="border-t border-slate-100 px-3 py-2 text-center">
                        <Cell state={state} title={parts.join(" · ")} testId={`grid-cell-${row.member_id}-${f.id}`} />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {visibleRows.length === 0 && (
                <tr><td colSpan={visibleForms.length + 1} className="px-4 py-8 text-center text-sm text-slate-400" data-testid="grid-no-rows">No members match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
